import React, { useContext, useRef, useState } from 'react';
import styles from './PassportGroup.module.scss';
import { IDocument, IPassportInfo } from 'src/shared/types/Documents';
import threeDotsIcon from 'src/assets/icons/three-dots.svg';
import { Button, Tag } from '@ds';
import checkIcon from 'src/assets/icons/check-circle.svg';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import DocumentsGrid from '../DocumentsGrid/DocumentsGrid';
import VerifyPassportModal from '../VerifyPassportModal/VerifyPassportModal';
import { ButtonSize, ButtonStyle } from '@ds/elements/Button/Button.types';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import unlinkIcon from 'src/assets/icons/arrows-clockwise.svg';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { IOption } from '@ds/elements/Select/Select.types';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';

interface CompletePassportProps {
  passportInfo: IPassportInfo;
  documents?: IDocument[];
  onDeletePassportGroup?: () => void;
  allCasePassports?: boolean;
}

const countFilledFields = (passportInfo: Partial<IPassportInfo>): number => {
  if (!passportInfo || typeof passportInfo !== 'object') {
    return 0;
  }
  const fieldsToCheck = [
    'country',
    'passportNumber',
    'issuedTo',
    'issuedDate',
    'issuingAuthority',
    'passportIssuedCountry',
    'passportExpirationDate',
    'issuedState',
    'issuedCity',
    'passportType',
    'locationOfPassport',
    'hasEnoughBlanks',
    'isTemporary',
    'isBiometric',
  ];
  return fieldsToCheck.reduce((count, field) => {
    if (
      passportInfo[field as keyof IPassportInfo] !== null &&
      passportInfo[field as keyof IPassportInfo] !== undefined &&
      passportInfo[field as keyof IPassportInfo] !== ''
    ) {
      return count + 1;
    }
    return count;
  }, 0);
};

const CompletePassport: React.FC<CompletePassportProps> = ({
  passportInfo,
  onDeletePassportGroup,
  documents,
  allCasePassports = false,
}) => {
  const menu = useRef<Menu>(null);
  const [showPassportModal, setShowPassportModal] = useState(false);
  const { beneMembers } = useContext(BeneDocAdditionalContext);
  const { caseId } = useContext(BeneDocAdditionalContext);
  const { data: caseDetails } = useGetCaseDetails(caseId ?? 0);
  const isCaseActive = caseDetails?.isActive ?? false;
  const expirationMessage = passportInfo?.passportExpirationDate
    ? `EXPIRES: ${getFormattedDate(passportInfo.passportExpirationDate.toString(), UserDateFormat.get())}`
    : 'N/A';

  const buildMenuModel = () => {
    const menuItems: MenuItem[] = [
      {
        label: 'Remove passport from case',
        icon: <img src={unlinkIcon} />,
        target: '_blank',
        disabled: false,
        command: () => onDeletePassportGroup && onDeletePassportGroup(),
        id: 'sb-passport-delete-btn',
        className: 'sb-clickable-menu-item',
      },
    ];
    return menuItems;
  };

  const createPassportName = () => {
    if (allCasePassports) {
      const beneMember = beneMembers?.find((member) => member.id === passportInfo?.issuedTo);
      return `Passport number: [${beneMember?.fullName}] #${passportInfo?.passportNumber} ${passportInfo?.country}`;
    } else {
      return `Passport number: #${passportInfo?.passportNumber} ${passportInfo?.country}`;
    }
  };

  const issuedToItems: IOption[] = React.useMemo(() => {
    if (beneMembers.length > 0) {
      return beneMembers.map((member) => ({
        label: member.fullName ?? '',
        value: member.id ?? -1,
      }));
    }

    return [{ key: -1, label: '', value: -1 }];
  }, [beneMembers]);

  return (
    <div className={styles['initial-passport']}>
      <div className={styles['header']}>
        <div className={styles['left-header']}>
          <p>{createPassportName()}</p>
          <Tag className={passportInfo?.passportExpirationDate ? styles['expiration-message-tag'] : ''}>
            {expirationMessage}
          </Tag>
        </div>
        <div className={styles['right-header']}>
          <div className={styles['answered-questions-container']}>
            <img src={checkIcon} className={styles['check-icon']} />
            <p>{`${passportInfo ? countFilledFields(passportInfo) : 0}/14 answered questions`}</p>
          </div>
          <span>
            {passportInfo.documents ? passportInfo?.documents?.length : documents?.length} document
            {((documents && documents?.length > 1) || (passportInfo.documents && passportInfo.documents?.length > 1)) &&
              's'}
          </span>
          {!allCasePassports && isCaseActive && (
            <>
              <Menu popup ref={menu} model={buildMenuModel()} className={styles['popup-menu-passport']} />
              <img
                src={threeDotsIcon}
                className={styles['dots-three-icon']}
                onClick={(e) => {
                  menu.current && menu.current.toggle(e);
                }}
              />
            </>
          )}
        </div>
      </div>
      <div className={styles['body']}>
        <DocumentsGrid
          documents={documents || passportInfo.documents || []}
          isPassport
          isReadonly
          showMenu={isCaseActive}
        />

        <div className={styles['view-button-container']}>
          <Button
            className={styles['view-button']}
            size={ButtonSize.large}
            styleType={ButtonStyle.secondary}
            onClick={() => setShowPassportModal(true)}
          >
            VIEW PASSPORT DETAILS
          </Button>
        </div>
        <VerifyPassportModal
          key={passportInfo?.passportId || 0}
          isReadOnly
          isVisible={showPassportModal}
          onClose={() => setShowPassportModal(false)}
          issuedToOptions={issuedToItems}
          initialFormData={
            passportInfo || {
              passportId: 0,
              country: '',
              passportNumber: '',
              issuedTo: 0,
              issuedDate: '' as unknown as Date,
              issuingAuthority: '',
              passportIssuedCountry: '',
              passportExpirationDate: '' as unknown as Date,
              issuedState: '',
              issuedCity: '',
              passportType: '',
              locationOfPassport: '',
              hasEnoughBlanks: false,
              isTemporary: false,
              isBiometric: false,
              isInactive: false,
            }
          }
        />
      </div>
    </div>
  );
};

export default CompletePassport;
